// src/domains/core/context-intelligence/registry/relationshipRegistry.ts

import type { Relationship } from "../types/relationships.types";

/**
 * Relationship Registry
 * Central registry for all known relationships between indicators
 */
class RelationshipRegistry {
  private relationships = new Map<string, Relationship>();

  /**
   * Register a new relationship
   *
   * @param relationship
   *
   */
  register(relationship: Relationship): void {
    this.relationships.set(relationship.id, relationship);
  }

  /**
   * Get a relationship by ID
   *
   * @param id
   *
   * @returns Relationship or undefined if not found
   *
   */
  get(id: string): Relationship | undefined {
    return this.relationships.get(id);
  }

  /**
   * Get all registered relationships
   *
   * @returns Array of all registered relationships
   *
   */
  getAll(): Relationship[] {
    return [...this.relationships.values()];
  }

  /**
   * Get relationships by type
   *
   * @param type
   *
   * @returns Array of relationships matching the specified type
   *
   */
  getByType(type: Relationship["type"]): Relationship[] {
    return this.getAll().filter((rel) => rel.type === type);
  }

  /**
   * Get relationships involving a specific indicator
   *
   * @param indicatorId
   *
   * @returns Array of relationships involving the specified indicator
   *
   */
  getByIndicator(indicatorId: string): Relationship[] {
    return this.getAll().filter(
      (rel) => rel.sourceIndicatorId === indicatorId || rel.targetIndicatorId === indicatorId,
    );
  }

  /**
   * Get enabled relationships only
   *
   * @returns Array of enabled relationships
   *
   */
  getEnabled(): Relationship[] {
    return this.getAll().filter((rel) => rel.enabled);
  }

  /**
   * Check if a relationship is registered
   *
   * @param id
   *
   * @returns True if relationship is registered, false otherwise
   *
   */
  has(id: string): boolean {
    return this.relationships.has(id);
  }
}

/**
 * Singleton Relationship Registry Instance
 */
export const relationshipRegistry = new RelationshipRegistry();

/**
 * Initialize default relationships for PoC
 */
export function initializeRelationshipRegistry(): void {
  // Health → Productivity Relationships
  relationshipRegistry.register({
    conditions: [
      {
        indicatorId: "oura_readiness",
        operator: ">",
        threshold: 70,
      },
      {
        indicatorId: "timetracq_task_completion_rate",
        operator: ">",
        threshold: 60,
      },
    ],
    confidence: 0.7,
    description: "Positive correlation between readiness score and task completion",
    direction: "positive",
    enabled: true,
    id: "readiness-productivity",
    metadata: {
      discoveredAt: Date.now(),
    },
    name: "Readiness ↔ Productivity",
    sourceIndicatorId: "oura_readiness",
    strength: "moderate",
    targetIndicatorId: "timetracq_task_completion_rate",
    type: "correlation",
  });

  relationshipRegistry.register({
    conditions: [
      {
        indicatorId: "oura_sleep_score",
        operator: "<",
        threshold: 70,
      },
    ],
    confidence: 0.8,
    description: "Sleep quality impacts task completion and focus",
    direction: "positive",
    enabled: true,
    id: "sleep-productivity",
    metadata: {
      discoveredAt: Date.now(),
    },
    name: "Sleep Quality ↔ Productivity",
    sourceIndicatorId: "oura_sleep_score",
    strength: "strong",
    targetIndicatorId: "timetracq_task_completion_rate",
    type: "causation",
  });

  relationshipRegistry.register({
    conditions: [
      {
        indicatorId: "oura_activity",
        operator: "<",
        threshold: 80,
      },
      {
        indicatorId: "withings_weight",
        operator: "trending-up",
        timeWindow: 7,
      },
    ],
    confidence: 0.65,
    description: "Inverse relationship between activity level and weight",
    direction: "negative",
    enabled: true,
    id: "activity-weight",
    metadata: {
      discoveredAt: Date.now(),
    },
    name: "Activity ↔ Weight",
    sourceIndicatorId: "oura_activity",
    strength: "moderate",
    targetIndicatorId: "withings_weight",
    type: "correlation",
  });
}
